import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { colors } from '@styleseat/brand';
import { isEqual as _equals } from 'underscore';
import {
  BodyText,
  Button,
  TextFormField,
  Stack,
  MaskedFormField,
} from '../../../ui';
import {
  Address,
  AddressServerErrors,
  AutocompleteAddressForm,
} from '../../../shared/AutocompleteAddressForm/AutocompleteAddressForm';
import { ProviderClientPhoneType } from '../../../../api/Providers/Clients/types';
import { formatBirthday } from '../../../../modules/provider/UIContact';
import { PartialProviderClient, updateContactFromInputs } from '../../../../modules/provider/Client';
import {
  isBirthdayValid as isBirthdayValidCheck,
  isEmailValid as isEmailValidCheck,
  isPhoneNumberValid,
} from '../../../../modules/provider/ClientFields';
import { Box } from '../../../../../../packages/ui';
import { Z_FG } from '../../../ui/utilities/zIndex';

type ProClientFormProps = {
  client?: PartialProviderClient;
  collapsible?: boolean;
  onChange: (client: PartialProviderClient) => void;
  onValidationChange: (isValid: boolean) => void;
  errors?: Record<string, string | boolean> & AddressServerErrors & {
    serverError?: boolean | string;
  };
};

const NAME_ERROR_MESSAGE = 'Please enter a name';
const BIRTHDAY_ERROR_MESSAGE = 'Please enter a valid birthday (e.g. 05/31)';
const EMAIL_ERROR_MESSAGE = 'Please enter a valid email address';
const PHONE_ERROR_MESSAGE = 'Please enter a valid phone number';

export const ProClientForm: React.FC<ProClientFormProps> = ({
  client,
  collapsible = false,
  onChange,
  errors,
  onValidationChange,
}) => {
  const {
    name: clientName,
    email: clientEmail,
    phones = [],
    address1,
    address2,
    city,
    state,
    zipcode,
  } = client || {};
  const mobilePhoneSettings = phones.find(phone => (
    phone.type === ProviderClientPhoneType.Mobile
  ));
  const homePhoneSettings = phones.find(phone => (
    phone.type === ProviderClientPhoneType.Home
  ));
  const workPhoneSettings = phones.find(phone => (
    phone.type === ProviderClientPhoneType.Work
  ));
  const [showAddMoreDetails, setShowAddMoreDetails] = useState<boolean>(!collapsible);
  const [name, setName] = useState<string>(clientName || '');
  const [email, setEmail] = useState<string>(clientEmail || '');
  const [mobilePhone, setMobilePhone] = useState<string>(mobilePhoneSettings?.number || '');
  const [homePhone, setHomePhone] = useState<string>(homePhoneSettings?.number || '');
  const [workPhone, setWorkPhone] = useState<string>(workPhoneSettings?.number || '');
  const [birthday, setBirthday] = useState<string>(formatBirthday(client) || '');
  const [clientAddress, setClientAddress] = useState<Address>({
    address: address1,
    address2,
    city,
    state,
    zipcode,
  });
  const [isBirthdayValid, setIsBirthdayValid] = useState<boolean>(!errors?.birthday);
  const [isEmailValid, setIsEmailValid] = useState<boolean>(!errors?.email);
  const [isAddressValid, setIsAddressValid] = useState<boolean>(true);
  const [isMobilePhoneValid, setIsMobilePhoneValid] = useState<boolean>(true);
  const [isHomePhoneValid, setIsHomePhoneValid] = useState<boolean>(true);
  const [isWorkPhoneValid, setIsWorkPhoneValid] = useState<boolean>(true);
  const isValid = useMemo(() => (
    isBirthdayValid
    && isEmailValid
    && isAddressValid
    && isMobilePhoneValid
    && isWorkPhoneValid
    && isHomePhoneValid
    && !!name
    && !Object.keys(errors || {}).find(k => k === 'serverError')
  ), [
    errors,
    isBirthdayValid,
    isAddressValid,
    isEmailValid,
    isMobilePhoneValid,
    isWorkPhoneValid,
    isHomePhoneValid,
    name,
  ]);

  useEffect(() => {
    onValidationChange?.(isValid);
  }, [
    isValid,
    onValidationChange,
  ]);

  const handleMobileNumberChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setMobilePhone(event.target.value.replace(/[()\-_\s]/g, ''));
  }, []);

  const handleHomeNumberChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setHomePhone(event.target.value.replace(/[()\-_\s]/g, ''));
  }, []);

  const handleWorkNumberChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setWorkPhone(event.target.value.replace(/[()\-_\s]/g, ''));
  }, []);

  const handleBirthdayChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setBirthday(event.target.value.replace(/[_\s]/g, ''));
  }, []);

  const handleToggleDetailsClick = useCallback(() => {
    setShowAddMoreDetails(prev => !prev);
  }, []);

  const handleBirthdayBlur = useCallback(() => {
    const hasBirthday = !!birthday.replace(/[/]/g, '');
    setIsBirthdayValid(!hasBirthday || isBirthdayValidCheck(birthday));
  }, [birthday]);

  const handleEmailChange = useCallback((newEmail: string) => {
    setIsEmailValid(true);
    setEmail(newEmail);
  }, []);

  const handleEmailBlur = useCallback(() => {
    setIsEmailValid(isEmailValidCheck(email));
  }, [email]);

  const handlePhoneBlur = useCallback(() => {
    setIsMobilePhoneValid(isPhoneNumberValid(mobilePhone));
    setIsHomePhoneValid(isPhoneNumberValid(homePhone));
    setIsWorkPhoneValid(isPhoneNumberValid(workPhone));
  }, [
    mobilePhone,
    homePhone,
    workPhone,
  ]);

  useEffect(() => {
    const newClient = updateContactFromInputs(
      client,
      {
        address: clientAddress,
        birthday,
        mobilePhoneNumber: mobilePhone,
        homePhoneNumber: homePhone,
        workPhoneNumber: workPhone,
        name,
        email,
      },
    );
    if (!_equals(newClient, client)) {
      onChange?.(newClient);
    }
    // Responding to the onChange callback will calculate this too often and
    // is unnecessary - we only need to update the client if client details change
    // Ideally the onChange callback is memoized, but we can't ensure that here so
    // leaving it out of deps will prevent infinite render loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clientAddress,
    birthday,
    name,
    email,
    homePhone,
    workPhone,
    mobilePhone,
    client,
  ]);

  return (
    <Stack space={16}>
      {errors?.serverError ? (
        <BodyText color={colors.red}>{errors.serverError}</BodyText>
      ) : null}
      <TextFormField
        label="Name"
        placeholder="Amara Jones"
        value={name}
        onChangeText={setName}
        type="text"
        accentColor={errors?.name ? colors.red : undefined}
        message={errors?.name ? NAME_ERROR_MESSAGE : undefined}
        testID="ProClientForm-input--name"
        name="name"
      />
      <TextFormField
        label="Email Address"
        placeholder="amara@email.com"
        value={email}
        onChangeText={handleEmailChange}
        onBlur={handleEmailBlur}
        type="email"
        accentColor={isEmailValid ? undefined : colors.red}
        message={isEmailValid ? undefined : EMAIL_ERROR_MESSAGE}
        testID="ProClientForm-input--email"
        name="email"
      />
      <MaskedFormField
        label="Mobile Number"
        mask="(999) 999-9999"
        placeholder="(___) ___-____"
        value={mobilePhone}
        onValueChange={handleMobileNumberChange}
        onBlur={handlePhoneBlur}
        accentColor={errors?.mobilePhone || !isMobilePhoneValid ? colors.red : undefined}
        message={errors?.mobilePhone || !isMobilePhoneValid ? PHONE_ERROR_MESSAGE : undefined}
        testID="ProClientForm-input--mobileNumber"
        name="mobilePhone"
      />
      {showAddMoreDetails ? (
        <Stack space={16} zIndex={1}>
          <MaskedFormField
            label="Home Number"
            mask="(999) 999-9999"
            placeholder="(___) ___-____"
            value={homePhone}
            onValueChange={handleHomeNumberChange}
            onBlur={handlePhoneBlur}
            accentColor={errors?.homePhone || !isHomePhoneValid ? colors.red : undefined}
            message={errors?.homePhone || !isHomePhoneValid ? PHONE_ERROR_MESSAGE : undefined}
            testID="ProClientForm-input--homeNumber"
          />
          {workPhoneSettings?.number ? (
            <MaskedFormField
              label="Work Number"
              mask="(999) 999-9999"
              placeholder="(___) ___-____"
              value={workPhone}
              onValueChange={handleWorkNumberChange}
              onBlur={handlePhoneBlur}
              accentColor={errors?.workPhone || !isWorkPhoneValid ? colors.red : undefined}
              message={errors?.workPhone || !isWorkPhoneValid ? PHONE_ERROR_MESSAGE : undefined}
              testID="ProClientForm-input--workNumber"
            />
          ) : null}
          <Box zIndex={Z_FG}>
            <AutocompleteAddressForm
              {...clientAddress}
              onChange={setClientAddress}
              onValidationChange={setIsAddressValid}
              errors={errors}
            />
          </Box>
          <MaskedFormField
            label="Birthday"
            type="tel"
            value={birthday}
            onValueChange={handleBirthdayChange}
            onBlur={handleBirthdayBlur}
            placeholder="MM/DD"
            mask="99/99"
            accentColor={isBirthdayValid ? undefined : colors.red}
            message={isBirthdayValid ? undefined : BIRTHDAY_ERROR_MESSAGE}
            testID="ProClientForm-input--birthday"
          />
        </Stack>
      ) : null}
      {collapsible ? (
        <Button
          appearance="silent"
          onClick={handleToggleDetailsClick}
          testID="ProClientForm-button--toggleDetails"
        >
          <BodyText
            color={colors.ash}
            appearance="small"
            weight="bold"
          >
            {showAddMoreDetails
              ? 'Less details'
              : 'Add more details'}
          </BodyText>
        </Button>
      ) : null}
    </Stack>
  );
};
